import { useEditorStore } from '@/store/editorStore';
import type { PolicyAction, RuleMatch } from '@/types/domain';
import './PolicyPanel.css';

function isQueryResult(r: unknown): r is import('@/types/domain').QueryResult {
  return typeof r === 'object' && r !== null && 'columns' in r;
}

function isPendingApproval(r: unknown): r is import('@/types/domain').PendingApprovalResponse {
  return typeof r === 'object' && r !== null && 'status' in r &&
    (r as { status: string }).status === 'pending_approval';
}

function isDenied(r: unknown): r is import('@/types/domain').DeniedResponse {
  return typeof r === 'object' && r !== null && 'status' in r &&
    (r as { status: string }).status === 'denied';
}

function actionClass(action: PolicyAction): string {
  switch (action) {
    case 'Deny': return 'deny';
    case 'RequireApproval': return 'approval';
    case 'Mask': return 'mask';
    case 'Warn': return 'warn';
    default: return 'allow';
  }
}

function RuleList({ rules }: { rules: RuleMatch[] }) {
  if (rules.length === 0) return null;
  return <>
    {rules.map(r => (
      <div className="rule-item" key={r.ruleId}>
        <span className="rule-id">{r.ruleId}</span>
        <span className={`rule-action ${actionClass(r.action)}`}>{r.action.toUpperCase()}</span>
        <div className="rule-desc">{r.reason}</div>
      </div>
    ))}
  </>;
}

// Aprovadores estáticos para demo (viriam do backend em um fluxo real)
const MOCK_APPROVERS = [
  { initials: 'JV', name: 'joão.varella',     status: 'online'  as const },
  { initials: 'MS', name: 'maria.santos',     status: 'idle'    as const },
  { initials: 'RT', name: 'ricardo.teixeira', status: 'offline' as const },
];

export function PolicyPanel() {
  const resp = useEditorStore(s => s.lastResponse);
  const isExecuting = useEditorStore(s => s.isExecuting);

  // ── Pending Approval ──────────────────────────────────────────────────────
  if (isPendingApproval(resp)) {
    return (
      <aside className="right-panel">
        <div className="panel-section">
          <div className="panel-title">Avaliação de política</div>
          <div className="policy-status approval">
            <div className="status-line">Aprovação necessária</div>
            <div className="status-desc">
              {resp.requirement.minApprovers} aprovador(es) com role{' '}
              <b style={{ color: 'var(--text-primary)' }}>
                {resp.requirement.approverRoles.join(', ')}
              </b>{' '}
              necessário(s). TTL: {resp.requirement.ttlMinutes} min.
            </div>
          </div>
          <RuleList rules={resp.rules} />
        </div>
        <div className="panel-section">
          <div className="panel-title">Aprovadores elegíveis</div>
          <div className="approvers">
            {MOCK_APPROVERS.map(a => (
              <div key={a.name} className="approver">
                <div className="avatar">{a.initials}</div>
                <span>{a.name}</span>
                <span className={`presence ${a.status}`}>● {a.status}</span>
              </div>
            ))}
          </div>
          <button className="request-btn">Solicitar aprovação →</button>
        </div>
        <RecentActivity />
      </aside>
    );
  }

  // ── Denied ────────────────────────────────────────────────────────────────
  if (isDenied(resp)) {
    return (
      <aside className="right-panel">
        <div className="panel-section">
          <div className="panel-title">Avaliação de política</div>
          <div className="policy-status deny">
            <div className="status-line">Execução negada</div>
            <div className="status-desc">{resp.reason}</div>
          </div>
          <RuleList rules={resp.rules} />
        </div>
        <RecentActivity />
      </aside>
    );
  }

  // ── Query Result (executed — show matched rules if any) ───────────────────
  if (isQueryResult(resp)) {
    const rules = resp.matchedRules ?? [];
    const maskedCols = Object.keys(resp.maskedColumns ?? {});
    const hasIssues = rules.length > 0;

    return (
      <aside className="right-panel">
        <div className="panel-section">
          <div className="panel-title">Avaliação de política</div>
          {hasIssues ? (
            <div className={`policy-status ${maskedCols.length > 0 ? 'mask' : 'allow'}`}>
              <div className="status-line">
                {maskedCols.length > 0 ? 'Executado com mascaramento' : 'Permitido com avisos'}
              </div>
              <div className="status-desc">
                {resp.status === 'Error'
                  ? resp.errorMessage
                  : `${resp.rows?.length ?? 0} linha(s) · ${resp.durationMs} ms`}
                {maskedCols.length > 0 && (
                  <> · Colunas mascaradas: <b style={{ color: 'var(--warn)' }}>{maskedCols.join(', ')}</b></>
                )}
              </div>
            </div>
          ) : (
            <div className="policy-status allow">
              <div className="status-line">Permitido</div>
              <div className="status-desc">
                {resp.status === 'Error'
                  ? resp.errorMessage
                  : `${resp.rows?.length ?? 0} linha(s) · ${resp.durationMs} ms`}
              </div>
            </div>
          )}
          <RuleList rules={rules} />
        </div>
        <RecentActivity />
      </aside>
    );
  }

  // ── Idle / executing ─────────────────────────────────────────────────────
  return (
    <aside className="right-panel">
      <div className="panel-section">
        <div className="panel-title">Avaliação de política</div>
        <div className="policy-idle">
          {isExecuting
            ? 'Avaliando políticas...'
            : 'Execute uma query para ver a avaliação de política.'
          }
        </div>
      </div>
      <RecentActivity />
    </aside>
  );
}

function RecentActivity() {
  return (
    <div className="panel-section">
      <div className="panel-title">Atividade recente</div>
      <div className="audit-log">
        <div className="audit-entry success">
          <span className="audit-time">14:28:14 · alice.rocha</span>
          <span className="audit-text"><b>SELECT</b> em <b>dbo.sales</b> — 142 linhas</span>
        </div>
        <div className="audit-entry warn">
          <span className="audit-time">13:55:02 · marcos.lima</span>
          <span className="audit-text"><b>UPDATE</b> aprovado por joão.varella — 5 linhas</span>
        </div>
        <div className="audit-entry denied">
          <span className="audit-time">11:09:47 · pedro.alves</span>
          <span className="audit-text"><b>DELETE</b> sem WHERE — <b style={{ color: 'var(--prod)' }}>bloqueado</b></span>
        </div>
      </div>
    </div>
  );
}
